import { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Users, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const JoinGroup = ({ user, onLogin }) => {
  const { groupId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteCode = searchParams.get('code');
  
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error, setError] = useState(null);
  const [alreadyMember, setAlreadyMember] = useState(false);

  useEffect(() => {
    fetchGroupInfo();
  }, [groupId, inviteCode]);

  const fetchGroupInfo = async () => {
    if (!inviteCode) {
      setError('Invalid invite link. Missing invite code.');
      setLoading(false);
      return;
    }

    try {
      // Try to get basic group info
      const response = await axios.get(`${API}/groups/${groupId}/public?invite_code=${inviteCode}`);
      setGroup(response.data);
      
      // Check if user is already a member
      if (user && response.data.member_ids?.includes(user.id)) {
        setAlreadyMember(true);
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setError('Group not found or invite link has expired.');
      } else if (error.response?.status === 400) {
        setError('Invalid invite code.');
      } else {
        setError('Unable to load group information.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!user) {
      // Store the join intent and redirect to login
      localStorage.setItem('pendingJoin', JSON.stringify({ groupId, inviteCode }));
      navigate('/login');
      toast.info('Please login first to join the group');
      return;
    }

    setJoining(true);
    try {
      await axios.post(`${API}/groups/${groupId}/join?user_id=${user.id}&invite_code=${inviteCode}`);
      toast.success(`Successfully joined ${group.name}!`);
      navigate(`/group/${groupId}`);
    } catch (error) {
      if (error.response?.data?.detail === 'User is already a member') {
        toast.info('You are already a member of this group');
        navigate(`/group/${groupId}`);
      } else {
        toast.error(error.response?.data?.detail || 'Failed to join group');
      }
    } finally {
      setJoining(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading group information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-2xl border border-border p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h1 className="text-xl font-bold mb-2">Unable to Join</h1>
          <p className="text-muted-foreground mb-6">{error}</p>
          <Button onClick={() => navigate('/')} className="rounded-full">
            Go to Home
          </Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950 flex items-center justify-center px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-card rounded-2xl border border-border p-8 max-w-md w-full"
      >
        <div className="text-center mb-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-violet-500 to-purple-500 flex items-center justify-center mx-auto mb-4">
            <Users className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-2xl font-bold mb-2">You're Invited!</h1>
          <p className="text-muted-foreground">Join this savings group on SharePay</p>
        </div>

        {group && (
          <div className="bg-muted/30 rounded-xl p-4 mb-6">
            <h2 className="font-semibold text-lg mb-2">{group.name}</h2>
            {group.description && (
              <p className="text-sm text-muted-foreground mb-3">{group.description}</p>
            )}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-background rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Goal</p>
                <p className="font-semibold">₹{group.goal_amount?.toLocaleString()}</p>
              </div>
              <div className="bg-background rounded-lg p-3">
                <p className="text-muted-foreground text-xs">Members</p>
                <p className="font-semibold">{group.member_ids?.length || 1} / {group.num_members}</p>
              </div>
              {group.daily_contribution > 0 && (
                <div className="bg-background rounded-lg p-3 col-span-2">
                  <p className="text-muted-foreground text-xs">Contribution</p>
                  <p className="font-semibold">₹{group.daily_contribution} / {group.contribution_frequency}</p>
                </div>
              )}
            </div>
          </div>
        )}

        {alreadyMember ? (
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-green-500 mb-4">
              <CheckCircle className="w-5 h-5" />
              <span className="font-medium">You're already a member!</span>
            </div>
            <Button onClick={() => navigate(`/group/${groupId}`)} className="w-full rounded-full gradient-primary">
              Go to Group
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            <Button 
              onClick={handleJoin} 
              disabled={joining}
              className="w-full rounded-full gradient-primary py-6"
            >
              {joining ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Joining...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  {user ? 'Join Group' : 'Login & Join'}
                </span>
              )}
            </Button>
            
            {!user && (
              <p className="text-xs text-muted-foreground text-center">
                You'll need to login or create an account to join
              </p>
            )}
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default JoinGroup;
